import React, { useState } from 'react';
import {
  useGetSupplyDemandReportQuery,
  useGetCountriesQuery,
  useGetCitiesQuery,
} from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFire,
  faScaleBalanced,
  faArrowTrendDown,
  faArrowTrendUp,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';

interface CategoryGap {
  id: number;
  title: string;
  section: string | null;
  supply: number;
  demand: number;
  gap: number;
  ratio: number | null;
  status: 'balanced' | 'surplus' | 'deficit';
  top_country: string | null;
}

const STATUS_CONFIG = {
  balanced: {
    label: 'Balanced',
    bg: '#fff7ed',
    color: '#c2410c',
    border: '#fb923c',
  },
  surplus: {
    label: 'Surplus',
    bg: '#f0fdf4',
    color: '#15803d',
    border: '#4ade80',
  },
  deficit: {
    label: 'Deficit',
    bg: '#fef2f2',
    color: '#b91c1c',
    border: '#f87171',
  },
};

const GapCard: React.FC<{ item: CategoryGap }> = ({ item }) => {
  const cfg = STATUS_CONFIG[item.status];
  const borderColor = cfg.border;

  return (
    <div
      className="card h-100"
      style={{
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      <div className="card-body p-5">
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between mb-4">
          <span
            className="badge fw-semibold fs-8 px-3 py-2"
            style={{
              background: cfg.bg,
              color: cfg.color,
              border: `1px solid ${borderColor}`,
              borderRadius: 6,
            }}
          >
            {cfg.label}
          </span>
          <span className="fw-bold text-dark fs-6 text-end ms-2" style={{ lineHeight: 1.3 }}>
            {item.title}
          </span>
        </div>

        {/* Data rows */}
        <div className="d-flex flex-column gap-2">
          <DataRow label="Section" value={item.section || '—'} />
          <DataRow label="Top Country" value={item.top_country || '—'} />
          <DataRow
            label="Supply (Offer)"
            value={`${item.supply.toLocaleString()} ads`}
            bold
          />
          <DataRow
            label="Demand"
            value={`${item.demand.toLocaleString()} ads`}
            bold
          />
          <DataRow
            label="Gap"
            value={item.gap > 0 ? `+${item.gap.toLocaleString()}` : item.gap.toLocaleString()}
            valueColor={item.gap > 0 ? '#15803d' : item.gap < 0 ? '#b91c1c' : '#374151'}
            bold
          />
          <DataRow
            label="Supply/Demand Ratio"
            value={item.ratio !== null ? `${item.ratio}x` : 'N/A'}
          />
        </div>
      </div>
    </div>
  );
};

const DataRow: React.FC<{
  label: string;
  value: string;
  bold?: boolean;
  valueColor?: string;
}> = ({ label, value, bold, valueColor }) => (
  <div className="d-flex align-items-center justify-content-between">
    <span
      className="fw-semibold"
      style={{ color: valueColor || '#111827', fontSize: 13, fontWeight: bold ? 700 : 600 }}
    >
      {value}
    </span>
    <span className="text-muted" style={{ fontSize: 12 }}>
      {label}
    </span>
  </div>
);

const SummaryCard: React.FC<{
  label: string;
  value: number;
  icon: any;
  iconBg: string;
  iconColor: string;
}> = ({ label, value, icon, iconBg, iconColor }) => (
  <div className="col-6 col-xl-3">
    <div className="card h-100">
      <div className="card-body p-6">
        <div className="d-flex align-items-start justify-content-between mb-4">
          <div />
          <div
            className="d-flex align-items-center justify-content-center rounded"
            style={{ width: 44, height: 44, background: iconBg, flexShrink: 0 }}
          >
            <FontAwesomeIcon icon={icon} style={{ color: iconColor, fontSize: 20 }} />
          </div>
        </div>
        <div className="fw-bold text-dark mb-1" style={{ fontSize: 32, lineHeight: 1 }}>
          {value}
        </div>
        <div className="text-muted fw-semibold fs-7">{label}</div>
      </div>
    </div>
  </div>
);

const AdminSupplyDemandReport: React.FC = () => {
  const [countryId, setCountryId] = useState('');
  const [cityId, setCityId] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'balanced' | 'surplus' | 'deficit'>('all');

  const params: Record<string, any> = {};
  if (countryId) params.country_id = countryId;
  if (cityId) params.city_id = cityId;

  const { data: reportData, isLoading } = useGetSupplyDemandReportQuery(params);
  const { data: countriesData } = useGetCountriesQuery({ per_page: 200 });
  const { data: citiesData } = useGetCitiesQuery(
    countryId ? { country_id: countryId, per_page: 500 } : { per_page: 500 }
  );

  const countries: any[] = countriesData?.data?.data || countriesData?.data || [];
  const cities: any[] = citiesData?.data?.data || citiesData?.data || [];

  const d = reportData?.data || {};
  const summary = d.summary || { total: 0, balanced: 0, deficit: 0, surplus: 0 };
  const allCategories: CategoryGap[] = d.categories || [];

  const filtered = allCategories.filter((c) => {
    const matchSearch = search === '' || c.title.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">

        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                Supply &amp; Demand Report
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted"><span className="text-muted">Home</span></li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">Reports &amp; Stats</li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">Supply &amp; Demand</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">

            {/* Filters */}
            <div className="card mb-6">
              <div className="card-body py-5">
                <div className="row g-4 align-items-end">
                  <div className="col-12 col-md-4">
                    <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">Country</label>
                    <select
                      className="form-select form-select-solid"
                      value={countryId}
                      onChange={(e) => { setCountryId(e.target.value); setCityId(''); }}
                    >
                      <option value="">All countries</option>
                      {countries.map((c: any) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">City</label>
                    <select
                      className="form-select form-select-solid"
                      value={cityId}
                      onChange={(e) => setCityId(e.target.value)}
                    >
                      <option value="">All cities</option>
                      {cities.map((c: any) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">Status</label>
                    <select
                      className="form-select form-select-solid"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as any)}
                    >
                      <option value="all">All statuses</option>
                      <option value="surplus">Surplus only</option>
                      <option value="balanced">Balanced only</option>
                      <option value="deficit">Deficit only</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {isLoading ? (
              <PageLoader />
            ) : (
              <>
                {/* Summary Cards */}
                <div className="row g-5 mb-8">
                  <SummaryCard
                    label="Categories Monitored"
                    value={summary.total}
                    icon={faFire}
                    iconBg="rgba(249,115,22,0.12)"
                    iconColor="#f97316"
                  />
                  <SummaryCard
                    label="Balanced"
                    value={summary.balanced}
                    icon={faScaleBalanced}
                    iconBg="rgba(59,130,246,0.12)"
                    iconColor="#3b82f6"
                  />
                  <SummaryCard
                    label="Deficit Gaps"
                    value={summary.deficit}
                    icon={faArrowTrendDown}
                    iconBg="rgba(239,68,68,0.12)"
                    iconColor="#ef4444"
                  />
                  <SummaryCard
                    label="Surplus Gaps"
                    value={summary.surplus}
                    icon={faArrowTrendUp}
                    iconBg="rgba(34,197,94,0.12)"
                    iconColor="#22c55e"
                  />
                </div>

                {/* Key Strategic Gaps */}
                <div className="card">
                  <div className="card-header border-0 pt-6 pb-0">
                    <div className="d-flex align-items-start justify-content-between w-100 flex-wrap gap-4">
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <span style={{ fontSize: 20 }}>🎯</span>
                          <h3 className="card-title fw-bold text-dark fs-3 mb-0">Key Strategic Gaps</h3>
                        </div>
                        <p className="text-muted fs-7 mb-0">
                          Supply and demand matrix for all categories — sorted by gap size
                        </p>
                      </div>
                      {/* Search */}
                      <div className="position-relative" style={{ minWidth: 220 }}>
                        <span
                          className="position-absolute top-50 translate-middle-y"
                          style={{ left: 12, color: '#9ca3af' }}
                        >
                          <FontAwesomeIcon icon={faSearch} style={{ fontSize: 13 }} />
                        </span>
                        <input
                          type="text"
                          className="form-control form-control-solid ps-9"
                          placeholder="Search category..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          style={{ height: 38, fontSize: 13 }}
                        />
                      </div>
                    </div>

                    {/* Status legend */}
                    <div className="d-flex align-items-center gap-4 mt-5 pb-4">
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                        <button
                          key={key}
                          className="btn btn-sm px-3 py-1"
                          style={{
                            background: statusFilter === key ? cfg.bg : 'transparent',
                            color: cfg.color,
                            border: `1px solid ${statusFilter === key ? cfg.border : '#e5e7eb'}`,
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                          onClick={() =>
                            setStatusFilter(statusFilter === key ? 'all' : (key as any))
                          }
                        >
                          {cfg.label}
                        </button>
                      ))}
                      {statusFilter !== 'all' && (
                        <button
                          className="btn btn-sm px-3 py-1 text-muted"
                          style={{ border: '1px solid #e5e7eb', borderRadius: 20, fontSize: 12 }}
                          onClick={() => setStatusFilter('all')}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="card-body pt-4">
                    {filtered.length === 0 ? (
                      <div className="d-flex align-items-center justify-content-center py-20">
                        <span className="text-muted fs-6">
                          {allCategories.length === 0
                            ? 'No supply or demand data found. Add some ads to categories first.'
                            : 'No categories match your filters.'}
                        </span>
                      </div>
                    ) : (
                      <div className="row g-5">
                        {filtered.map((item) => (
                          <div key={item.id} className="col-12 col-md-6 col-xl-3">
                            <GapCard item={item} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSupplyDemandReport;
